import React from 'react';
import { Box, Typography, Card, Divider } from '@mui/joy';
import type { RoofFeature } from '../../types';
import { LINE_TYPE_CONFIG, LINE_TYPES, PITCH_MULTIPLIERS } from './lineDrawingConstants';

interface LinearTotalsPanelProps {
  features: RoofFeature[];
  pitch?: string;
  wasteFactor?: number;
  flatArea?: number;
  perimeterFt?: number; // Actual polygon perimeter from roof sections
}

export const LinearTotalsPanel: React.FC<LinearTotalsPanelProps> = ({
  features,
  pitch,
  wasteFactor,
  flatArea,
  perimeterFt,
}) => {
  // Aggregate totals by type
  const totals = LINE_TYPES.map((type) => {
    const config = LINE_TYPE_CONFIG[type];
    const typeFeatures = features.filter((f) => f.feature_type === type);
    const totalLength = typeFeatures.reduce(
      (sum, f) => sum + Number(f.length_ft || 0),
      0
    );
    return {
      type,
      label: config.label,
      color: config.color,
      totalLength,
      count: typeFeatures.length,
    };
  }).filter((t) => t.count > 0);

  const grandTotal = totals.reduce((sum, t) => sum + t.totalLength, 0);

  // Perimeter: use polygon perimeter if available, otherwise sum eave + rake lines
  const eaveTotal = totals.find((t) => t.type === 'eave')?.totalLength || 0;
  const rakeTotal = totals.find((t) => t.type === 'rake')?.totalLength || 0;
  const perimeterTotal = perimeterFt || (eaveTotal + rakeTotal);

  // Pitch-adjusted calculations
  const multiplier = pitch ? PITCH_MULTIPLIERS[pitch] || 1 : 1;
  const trueArea = (flatArea || 0) * multiplier;
  const wasteMultiplier = 1 + (wasteFactor || 0) / 100;
  const withWaste = trueArea * wasteMultiplier;
  const squares = trueArea / 100;

  if (totals.length === 0 && !flatArea) {
    return null;
  }

  return (
    <Card variant="outlined" size="sm">
      {/* Area Totals */}
      {flatArea != null && flatArea > 0 && (
        <>
          <Typography
            level="title-sm"
            sx={{
              textTransform: 'uppercase',
              fontSize: '11px',
              fontWeight: 'bold',
              letterSpacing: '0.5px',
              color: 'text.primary',
            }}
          >
            Area Totals
          </Typography>
          <Box sx={{ mt: 1 }}>
            <SummaryRow label="Flat Area" value={`${flatArea.toLocaleString(undefined, { maximumFractionDigits: 0 })} sf`} />
            <SummaryRow
              label={`True Area`}
              value={`${trueArea.toLocaleString(undefined, { maximumFractionDigits: 0 })} sf`}
              highlight
              sublabel={pitch ? `×${multiplier}` : undefined}
            />
            <SummaryRow label="Squares" value={squares.toFixed(1)} highlight />
            <Divider sx={{ my: 0.5 }} />
            <SummaryRow
              label="With Waste"
              value={`${withWaste.toLocaleString(undefined, { maximumFractionDigits: 0 })} sf`}
              highlight
              sublabel={wasteFactor ? `+${wasteFactor}%` : undefined}
            />
            <SummaryRow label="Eave/Perimeter" value={`${perimeterTotal.toFixed(1)} lf`} />
          </Box>
          <Divider sx={{ my: 1 }} />
        </>
      )}

      {/* Linear totals */}
      {totals.length > 0 && (
        <>
          <Typography
            level="title-sm"
            sx={{
              textTransform: 'uppercase',
              fontSize: '11px',
              fontWeight: 'bold',
              letterSpacing: '0.5px',
              color: 'text.primary',
            }}
          >
            Linear (for ordering)
          </Typography>

          <Box sx={{ mt: 1 }}>
            {totals.map((total) => (
              <Box
                key={total.type}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 0.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 3,
                      backgroundColor: total.color,
                      borderRadius: 1,
                    }}
                  />
                  <Typography level="body-xs">{total.label}</Typography>
                </Box>
                <Typography
                  level="body-xs"
                  sx={{ fontWeight: 'bold', color: 'text.primary' }}
                >
                  {total.totalLength.toFixed(1)} lf
                </Typography>
              </Box>
            ))}

            <Divider sx={{ my: 0.5 }} />

            <SummaryRow label="Total" value={`${grandTotal.toFixed(1)} lf`} highlight />
          </Box>

          <Typography
            level="body-xs"
            sx={{ mt: 1, color: 'text.tertiary', fontSize: '10px' }}
          >
            Ridge+Hip = ridge cap needed. Eave = drip edge needed.
          </Typography>
        </>
      )}
    </Card>
  );
};

function SummaryRow({
  label,
  value,
  highlight,
  sublabel,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  sublabel?: string;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography level="body-xs" sx={{ fontWeight: highlight ? 'bold' : 'normal' }}>
          {label}
        </Typography>
        {sublabel && (
          <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '10px' }}>
            ({sublabel})
          </Typography>
        )}
      </Box>
      <Typography
        level="body-xs"
        sx={{ fontWeight: 'bold', color: highlight ? 'text.primary' : undefined }}
      >
        {value}
      </Typography>
    </Box>
  );
}
